import * as React from "react";
import { Severity } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";

interface PhysicalIndexStats {
  name: string;
  documentCount: number;
  storageMb: number;
  isShared: boolean;
  isDedicated: boolean;
}

interface SiteFacet {
  siteId: string;
  documentCount: number;
  assignmentType: string; // "shared" | "dedicated" | "legacy-fallback"
}

interface SharedIndexStats {
  indexName: string;
  totalDocuments: number;
  storageMb: number;
  sites: SiteFacet[];
}

interface DedicatedIndexStats {
  indexName: string;
  siteId: string;
  documentCount: number;
  storageMb: number;
}

interface AssignmentSummary {
  siteId: number;
  indexName: string;
  indexType: string;
  assignedAt: string;
}

interface IndexStatsResponseV2 {
  sharedIndexes: SharedIndexStats[];
  dedicatedIndexes: DedicatedIndexStats[];
  allIndexes: PhysicalIndexStats[];
  totalSlots: number;
  assignments: AssignmentSummary[];
}

interface IndexTypeFacet {
  indexName: string;
  count: number;
}

interface SiteIndexBreakdown {
  siteId: string;
  totalDocuments: number;
  indexTypes: IndexTypeFacet[];
  physicalIndex: string;
  assignmentType: string;
}

// Azure Search Standard S1 = 160GB per index
const AZURE_INDEX_LIMIT_MB = 160 * 1024;
const WARN_PCT = 70;
const DANGER_PCT = 90;

function storageColor(storageMb: number): string {
  const pct = (storageMb / AZURE_INDEX_LIMIT_MB) * 100;
  if (pct >= DANGER_PCT) return "text-red-600 font-bold";
  if (pct >= WARN_PCT)   return "text-yellow-600 font-semibold";
  return "text-green-700";
}

function StorageBar({ storageMb }: { storageMb: number }) {
  const pct = Math.min((storageMb / AZURE_INDEX_LIMIT_MB) * 100, 100);
  const color = pct >= DANGER_PCT ? "bg-red-500" : pct >= WARN_PCT ? "bg-yellow-400" : "bg-green-500";
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 bg-gray-200 rounded-full h-1.5">
        <div className={`${color} rounded-full h-1.5`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs ${storageColor(storageMb)}`}>{storageMb.toFixed(1)} MB ({pct.toFixed(1)}%)</span>
    </div>
  );
}

export default function SearchIndexes() {
  const api = useApi();
  const { displayToast } = useToastAlert();
  const [loading, setLoading] = React.useState(true);
  const [stats, setStats] = React.useState<IndexStatsResponseV2 | null>(null);
  const [expandedSite, setExpandedSite] = React.useState<string | null>(null);
  const [siteBreakdown, setSiteBreakdown] = React.useState<Record<string, SiteIndexBreakdown>>({});
  const [loadingSite, setLoadingSite] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<"shared" | "dedicated" | "all">("shared");
  const [provisioningSite, setProvisioningSite] = React.useState<string | null>(null);

  async function provisionDedicated(siteId: string) {
    if (!window.confirm(
      `Provision a dedicated index for site ${siteId}?\n\n` +
      `This will move the site off the shared index. ` +
      `Content will NOT be automatically re-ingested — you will need to trigger a re-ingest manually after provisioning.`
    )) return;

    setProvisioningSite(siteId);
    try {
      const resp = await api.admin.post("DatabaseTool/ProvisionDedicatedIndex", { SiteId: parseInt(siteId) });
      if (!resp.ok) {
        const err = await resp.json();
        displayToast(Severity.Error, err.error ?? "Provisioning failed.");
        return;
      }
      const result = await resp.json();
      displayToast(Severity.Success, `Dedicated index '${result.indexName}' provisioned. Re-ingest content to populate it.`);
      // Reload stats
      const data: IndexStatsResponseV2 = await (await api.admin.get("DatabaseTool/SearchIndexStats")).json();
      setStats(data);
    } catch {
      displayToast(Severity.Error, "Failed to provision dedicated index.");
    } finally {
      setProvisioningSite(null);
    }
  }

  React.useEffect(() => {
    (async () => {
      try {
        const data: IndexStatsResponseV2 = await (
          await api.admin.get("DatabaseTool/SearchIndexStats")
        ).json();
        setStats(data);
      } catch {
        displayToast(Severity.Error, "Failed to load search index stats");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function toggleSite(siteId: string) {
    if (expandedSite === siteId) { setExpandedSite(null); return; }
    setExpandedSite(siteId);
    if (siteBreakdown[siteId]) return;
    setLoadingSite(siteId);
    try {
      const data: SiteIndexBreakdown = await (
        await api.admin.get(`DatabaseTool/SearchIndexSite?siteId=${siteId}`)
      ).json();
      setSiteBreakdown(prev => ({ ...prev, [siteId]: data }));
    } catch {
      displayToast(Severity.Error, `Failed to load breakdown for site ${siteId}`);
    } finally {
      setLoadingSite(null);
    }
  }

  if (loading) return <LoadingSpinner />;
  if (!stats)  return <div className="p-6 text-red-600">Failed to load stats.</div>;

  const { sharedIndexes, dedicatedIndexes, allIndexes, totalSlots, assignments } = stats;
  const totalStorageMb = allIndexes.reduce((s, i) => s + i.storageMb, 0);
  const totalDocs      = allIndexes.reduce((s, i) => s + i.documentCount, 0);
  const totalSites     = sharedIndexes.reduce((s, i) => s + (i.sites?.length ?? 0), 0) + dedicatedIndexes.length;
  const legacyCount    = sharedIndexes
    .flatMap(s => s.sites ?? [])
    .filter(s => s.assignmentType === "legacy-fallback").length;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* ── Summary Cards ─────────────────────────────────── */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <StatCard label="Physical Indexes Used" value={`${totalSlots} / 100`} />
        <StatCard label="Total Documents"       value={totalDocs.toLocaleString()} />
        <StatCard label="Total Storage"         value={`${(totalStorageMb / 1024).toFixed(2)} GB`} />
        <StatCard label="Sites Tracked"         value={totalSites.toString()} />
        <StatCard label="Legacy Fallback Sites" value={legacyCount.toString()} warn={legacyCount > 0} />
      </div>

      {legacyCount > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-sm text-yellow-800">
          ⚠ <strong>{legacyCount} site(s)</strong> have no <code>TenantIndexAssignment</code> record and fall back to
          <code> velaro-shared-index</code>. These are pre-Feb-2026 sites. Their data shows under the shared index above.
        </div>
      )}

      {/* ── Tabs ──────────────────────────────────────────── */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        <TabButton active={activeTab === "shared"}    onClick={() => setActiveTab("shared")}>
          Shared Indexes ({sharedIndexes.length})
        </TabButton>
        <TabButton active={activeTab === "dedicated"} onClick={() => setActiveTab("dedicated")}>
          Dedicated ({dedicatedIndexes.length})
        </TabButton>
        <TabButton active={activeTab === "all"}       onClick={() => setActiveTab("all")}>
          All Physical ({allIndexes.length})
        </TabButton>
      </div>

      {activeTab === "shared" && (
        <SharedIndexesPanel
          sharedIndexes={sharedIndexes}
          expandedSite={expandedSite}
          loadingSite={loadingSite}
          siteBreakdown={siteBreakdown}
          onToggleSite={toggleSite}
          provisioningSite={provisioningSite}
          onProvisionDedicated={provisionDedicated}
        />
      )}

      {activeTab === "dedicated" && (
        <DedicatedIndexesPanel dedicatedIndexes={dedicatedIndexes} />
      )}

      {activeTab === "all" && (
        <AllIndexesPanel allIndexes={allIndexes} />
      )}
    </div>
  );
}

// ── Shared Indexes Panel ───────────────────────────────────────────────────────

function SharedIndexesPanel({
  sharedIndexes, expandedSite, loadingSite, siteBreakdown, onToggleSite, provisioningSite, onProvisionDedicated
}: {
  sharedIndexes: SharedIndexStats[];
  expandedSite: string | null;
  loadingSite: string | null;
  siteBreakdown: Record<string, SiteIndexBreakdown>;
  onToggleSite: (siteId: string) => void;
  provisioningSite: string | null;
  onProvisionDedicated: (siteId: string) => void;
}) {
  if (!sharedIndexes.length) return <div className="text-gray-500 p-4">No shared indexes found.</div>;

  return (
    <div className="space-y-6">
      {sharedIndexes.map(idx => (
        <div key={idx.indexName} className="border border-gray-200 rounded-lg overflow-hidden">
          {/* Index header */}
          <div className="p-4 bg-blue-50 border-b border-blue-200 flex flex-wrap gap-6 items-center">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Index</div>
              <div className="font-mono font-semibold text-blue-700">{idx.indexName}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Customers</div>
              <div className="font-semibold">{idx.sites?.length ?? 0}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Documents</div>
              <div className="font-semibold">{idx.totalDocuments.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Storage (160 GB limit)</div>
              <StorageBar storageMb={idx.storageMb} />
            </div>
          </div>

          {/* Sites table */}
          {(!idx.sites || idx.sites.length === 0) ? (
            <div className="p-4 text-gray-400 text-sm">No sites in this index.</div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-2 font-medium text-gray-600">Site ID</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Documents</th>
                  <th className="px-4 py-2 font-medium text-gray-600">% of Index</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Assignment</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Detail</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {idx.sites.map(site => {
                  const pct = idx.totalDocuments > 0
                    ? ((site.documentCount / idx.totalDocuments) * 100).toFixed(1) : "0";
                  const isExpanded = expandedSite === site.siteId;
                  const breakdown  = siteBreakdown[site.siteId];

                  return (
                    <React.Fragment key={site.siteId}>
                      <tr className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono font-semibold text-blue-700">{site.siteId}</td>
                        <td className="px-4 py-2">{site.documentCount.toLocaleString()}</td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-gray-200 rounded-full h-1.5">
                              <div className="bg-blue-500 rounded-full h-1.5" style={{ width: `${Math.min(parseFloat(pct), 100)}%` }} />
                            </div>
                            <span className="text-gray-500 text-xs">{pct}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <AssignmentBadge type={site.assignmentType} />
                        </td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => onToggleSite(site.siteId)}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            {isExpanded ? "▼ hide" : "▶ expand"}
                          </button>
                        </td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => onProvisionDedicated(site.siteId)}
                            disabled={provisioningSite === site.siteId}
                            className="text-xs bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-2 py-1 rounded"
                            title="Move this site to its own dedicated Azure Search index. Requires re-ingest after provisioning."
                          >
                            {provisioningSite === site.siteId ? "Provisioning…" : "Dedicated Index"}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-blue-50 border-t border-blue-100">
                          <td colSpan={6} className="px-8 py-3">
                            {loadingSite === site.siteId ? <LoadingSpinner /> : breakdown ? (
                              <div>
                                <div className="text-xs text-gray-500 mb-2">
                                  Physical index: <span className="font-mono">{breakdown.physicalIndex}</span>
                                  {" · "}Assignment: <AssignmentBadge type={breakdown.assignmentType} />
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {breakdown.indexTypes.map(t => (
                                    <div key={t.indexName} className="bg-white rounded border border-blue-200 px-3 py-1.5 text-xs">
                                      <span className="font-mono font-semibold text-blue-700">{t.indexName}</span>
                                      <span className="ml-2 text-gray-500">{t.count.toLocaleString()} docs</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Dedicated Indexes Panel ────────────────────────────────────────────────────

function DedicatedIndexesPanel({ dedicatedIndexes }: { dedicatedIndexes: DedicatedIndexStats[] }) {
  if (!dedicatedIndexes.length) return <div className="text-gray-500 p-4">No dedicated indexes provisioned.</div>;

  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-gray-50 text-left">
          <th className="px-4 py-2 font-medium text-gray-600">Site ID</th>
          <th className="px-4 py-2 font-medium text-gray-600">Index Name</th>
          <th className="px-4 py-2 font-medium text-gray-600">Documents</th>
          <th className="px-4 py-2 font-medium text-gray-600">Storage</th>
        </tr>
      </thead>
      <tbody>
        {dedicatedIndexes.map(idx => (
          <tr key={idx.indexName} className="border-t border-gray-100 hover:bg-gray-50">
            <td className="px-4 py-2 font-mono font-semibold text-blue-700">{idx.siteId}</td>
            <td className="px-4 py-2 font-mono text-xs">{idx.indexName}</td>
            <td className="px-4 py-2">{idx.documentCount.toLocaleString()}</td>
            <td className="px-4 py-2"><StorageBar storageMb={idx.storageMb} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── All Indexes Panel ──────────────────────────────────────────────────────────

function AllIndexesPanel({ allIndexes }: { allIndexes: PhysicalIndexStats[] }) {
  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-gray-50 text-left">
          <th className="px-4 py-2 font-medium text-gray-600">Index Name</th>
          <th className="px-4 py-2 font-medium text-gray-600">Documents</th>
          <th className="px-4 py-2 font-medium text-gray-600">Storage</th>
          <th className="px-4 py-2 font-medium text-gray-600">Type</th>
        </tr>
      </thead>
      <tbody>
        {allIndexes.map(idx => (
          <tr key={idx.name} className="border-t border-gray-100 hover:bg-gray-50">
            <td className="px-4 py-2 font-mono text-xs font-semibold text-blue-700">{idx.name}</td>
            <td className="px-4 py-2">{idx.documentCount.toLocaleString()}</td>
            <td className="px-4 py-2"><StorageBar storageMb={idx.storageMb} /></td>
            <td className="px-4 py-2">
              {idx.isShared    && <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded">shared</span>}
              {idx.isDedicated && <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded">dedicated</span>}
              {!idx.isShared && !idx.isDedicated && <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded">legacy</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function AssignmentBadge({ type }: { type: string }) {
  if (type === "dedicated")       return <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded">dedicated</span>;
  if (type === "shared")          return <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded">shared</span>;
  if (type === "legacy-fallback") return <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-0.5 rounded">legacy</span>;
  return <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded">{type}</span>;
}

function StatCard({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className={`rounded-lg border p-4 ${warn ? "bg-yellow-50 border-yellow-300" : "bg-white border-gray-200"}`}>
      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</div>
      <div className={`text-2xl font-bold ${warn ? "text-yellow-700" : "text-gray-800"}`}>{value}</div>
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
    >
      {children}
    </button>
  );
}
